import React, { useCallback, useRef } from 'react';
import InputComponent from '@components/Input';
import { Box, Button, HStack, Select } from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { updateGomsOrdersReqData } from '@store/actions/reqDataActions';
import { getGomsOrders } from '@store/actions/ordersActions';
import useDidMountEffect from '@/hooks/useDidMount';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '@/hooks/useIsMobile';
import OrdersList from '@components/OrdersList';
import { ORDER_RESOURCE, ORDER_TYPE_V2 } from '@constants';


const GomsOrdersContent = ({ 
  defaultOrderType = '', 
  title = 'Search Orders',
  placeholder = 'Search by Client ID or Deal ID',
  showOrderTypeFilter = true,
  autoSearchOnMount = false
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchTimer = useRef(null);
  const isMobile = useIsMobile();

  const searchReqData = useSelector((state) => state.reqData.dashboard.gomsOrders);

  const searchResult = useSelector((state) => ({
    orders: state.resData.gomsOrders.data[searchReqData.pageNo] || [],
    isLoading: state.resData.gomsOrders.isLoading,
    isError: state.resData.gomsOrders.isError,
    totalPages: state.resData.gomsOrders.totalPages
  }));

  // Auto-fetch orders when component mounts with default order type
  React.useEffect(() => {
    if (defaultOrderType && (!searchReqData.orderType || searchReqData.orderType !== defaultOrderType)) {
      dispatch(updateGomsOrdersReqData({ 
        orderType: defaultOrderType,
        query: '',
        pageNo: 0,
        pageSize: 10
      }));
    } else if (autoSearchOnMount) {
      searchOrders(true);
    }
  }, [defaultOrderType, autoSearchOnMount]);

  //refetch orders data on change in username query, with a delay of 2 seconds
  useDidMountEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        searchOrders(true);
      }, 2000);
    } else {
      searchOrders(true);
      searchTimer.current = true;
    }
  }, [searchReqData.query]);

  useDidMountEffect(() => {
    searchOrders();
  }, [searchReqData.pageNo]);

  useDidMountEffect(() => {
    searchOrders(true);
  }, [searchReqData.orderType]);

  const searchOrders = async (refresh = false) => {
    if (refresh || !searchResult.orders?.length) {
      // Ensure the default order type is always used if specified
      const searchParams = defaultOrderType 
        ? { ...searchReqData, orderType: defaultOrderType, refresh }
        : { ...searchReqData, refresh };
      
      dispatch(getGomsOrders(searchParams));
    }
  };

  const handleSearchReqChange = useCallback(
    (e) => {
      if (e.target.value === searchReqData.query) return;
      dispatch(updateGomsOrdersReqData({ query: e.target.value }));
    },
    [dispatch, searchReqData.query]
  );

  const handleOrderTypeChange = useCallback((e) => {
    e.target.value !== searchReqData.orderType &&
    dispatch(
      updateGomsOrdersReqData({
        orderType: e.target.value
      })
    );
  });

  const handlePageChange = useCallback(
    (pageNo) => {
      pageNo - 1 !== searchReqData.pageNo &&
      dispatch(updateGomsOrdersReqData({ pageNo: pageNo - 1 }));
    },
    [dispatch, searchReqData.pageNo]
  );

  const onOrderDetailsClick = useCallback(
    (orderId, userId, orderResource) => {
      navigate(
        isMobile ? `/order/${orderId}?orderResource=${orderResource}` : `/user/${userId}?orderId=${orderId}&orderResource=${orderResource}`
      );
    },
    [isMobile, navigate]
  );

  return (
    <>
      <InputComponent
        type="text"
        value={searchReqData.query}
        name="search"
        handleChange={handleSearchReqChange}
        placeholder={placeholder}
        inputTitle={title}
        leftElement={<MdSearch size="2rem" color="white" />}
        width="40rem"
        maxWidth="100%"
        autoComplete={'off'}
        spellcheck={false}
        autoCorrect={'off'}
      />

      <HStack
        w={'100%'}
        justifyContent={'space-between'}
        maxW={'40rem'}
        alignSelf={'flex-start'}
      >
        <Button
          type="submit"
          color="black"
          rounded="lg"
          colorScheme="brand"
          my={5}
          py={2}
          onClick={() => searchOrders()}
          fontWeight={600}
          isLoading={searchResult.isLoading}
          alignSelf="start"
          style={{
            marginTop: '1em'
          }}
          size={{
            base: 'md',
            md: 'lg'
          }}
        >
          Search
        </Button>
        {showOrderTypeFilter && (
          <Box maxWidth="15rem">
            <Select
              placeholder={`Filter Order Type`}
              onChange={handleOrderTypeChange}
              name={'orderType'}
              value={searchReqData.orderType}
              bg="black"
              color="gray"
              size={'sm'}
              rounded={'lg'}
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(ORDER_TYPE_V2).map((orderType) => (
                <option value={orderType} key={orderType}>
                  {orderType}
                </option>
              ))}
            </Select>
          </Box>
        )}
      </HStack>

      <OrdersList
        orders={searchResult.orders}
        pageNo={searchReqData.pageNo}
        totalPages={searchResult.totalPages}
        handlePageChange={handlePageChange}
        onOrderDetailsClick={onOrderDetailsClick}
        show={'userId'}
        orderResource={ORDER_RESOURCE.GOMS_SERVICE}
      />
    </>
  );
};

export default GomsOrdersContent;
